import { ArrowLeft, Droplets, Dna, Link2, Network, Sparkles, Utensils } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PolymersAdditionMode } from './PolymersAdditionMode';
import { FOOD_DATA_META } from './polymersBiomoleculesFood';
import {
  POLYMER_DATA_META,
  POLYMER_MODE_ORDER,
  POLYMER_YEAR_DEFAULTS,
  POLYMERS_TOOL_IDENTITY,
  type PolymerMode,
} from './polymersBiomolecules';
import { PolymersBiomoleculesMode } from './PolymersBiomoleculesMode';
import { PolymersCondensationMode } from './PolymersCondensationMode';
import { PolymersFoodEnzymesMode } from './PolymersFoodEnzymesMode';
import { PolymersSoapsMode } from './PolymersSoapsMode';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  PolymerMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  addition: {
    tab: 'Addition',
    eyebrow: 'Mode 1 · addition polymers',
    title: 'Open the C=C and link the monomers',
    description:
      'Predict the repeat unit from an alkene monomer, or work back from a repeat unit to its monomer.',
  },
  condensation: {
    tab: 'Condensation',
    eyebrow: 'Mode 2 · condensation polymers',
    title: 'Two reacting groups, one small molecule lost',
    description:
      'Predict the new link and the small molecule eliminated when polyesters and polyamides form.',
  },
  biomolecules: {
    tab: 'Biomolecules',
    eyebrow: 'Mode 3 · natural polymers',
    title: 'Proteins, carbohydrates and lipids',
    description:
      'Join amino acids, compare protein structure levels, condense sugars and build triglycerides.',
  },
  soaps: {
    tab: 'Soaps',
    eyebrow: 'Mode 4 · soaps and detergents',
    title: 'From fat to soap, and why hard water matters',
    description:
      'Saponify a fat, compare soap and detergent heads, build a micelle and predict hard-water scum.',
  },
  'food-enzymes': {
    tab: 'Food & enzymes',
    eyebrow: 'Mode 5 · food chemistry and enzymes',
    title: 'Enzymes, digestion and food energy',
    description:
      'Predict how temperature, pH and inhibitors change enzyme activity, hydrolyse food molecules and calculate food energy.',
  },
};

const MODE_ICONS: Record<PolymerMode, typeof Link2> = {
  addition: Link2,
  condensation: Network,
  biomolecules: Dna,
  soaps: Droplets,
  'food-enzymes': Utensils,
};

export function PolymersBiomoleculesLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<PolymerMode>(() => modeForYear(POLYMER_YEAR_DEFAULTS, year));
  const copy = MODE_COPY[mode];
  const ModeIcon = MODE_ICONS[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'polymers-biomolecules', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-mint/30 bg-gradient-to-br from-wash-mint via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:p-8"
        data-tool-key={POLYMERS_TOOL_IDENTITY.toolKey}
        data-tool-version={POLYMERS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-mint">
              <Network className="h-4 w-4" aria-hidden="true" /> Organic chemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Polymers &amp; Biomolecules Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              See how small molecules join into long chains: by opening a double bond, or by
              condensing two reacting groups and losing water.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Years 11–12 · Explore tool
          </div>
        </div>

        <div
          className="mt-6 grid max-w-4xl grid-cols-2 gap-1 rounded-[1.5rem] bg-white/75 p-1 sm:grid-cols-5 sm:rounded-full"
          role="group"
          aria-label="Choose a mode"
        >
          {POLYMER_MODE_ORDER.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-2 py-2.5 text-sm font-extrabold transition-colors motion-reduce:transition-none sm:px-3 ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            <ModeIcon className="h-4 w-4" aria-hidden="true" />
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'addition' ? <PolymersAdditionMode /> : null}
      {mode === 'condensation' ? <PolymersCondensationMode /> : null}
      {mode === 'biomolecules' ? <PolymersBiomoleculesMode /> : null}
      {mode === 'soaps' ? <PolymersSoapsMode /> : null}
      {mode === 'food-enzymes' ? <PolymersFoodEnzymesMode /> : null}

      <aside className="mt-6 flex items-start gap-3 rounded-3xl border border-brand-mint/25 bg-wash-mint p-5">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-brand-mint" aria-hidden="true" />
        <p className="text-sm font-bold leading-6 text-slate2">
          Model boundary: structures are simplified condensed formulae and two-carbon drawings
          generated from reviewed data. Chain length, tacticity, branching, ring structures of
          sugars, reaction conditions and catalysts are not modelled. Micelles are drawn as a flat
          cross-section with a fixed number of ions.{' '}
          {mode === 'food-enzymes' ? FOOD_DATA_META.conditions : POLYMER_DATA_META.conditions}
        </p>
      </aside>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases (status: pending teaching review). It does
        not save an attempt, award Stars, record mastery or claim alignment to a state syllabus.
        Making soap uses hot, concentrated alkali, which is corrosive: practical work needs teacher
        supervision and a risk assessment.
      </footer>
    </StudyShell>
  );
}
